/**
 * cart-loyalty.js — Redeem-your-points widget for the cart page AND the
 * cart drawer. Idempotent per-widget, and re-scans on cart updates so the
 * widget is re-initialised after the drawer's footer is re-rendered by
 * AJAX cart merges.
 *
 * Two-mode picker:
 *   - "Select Discount" (Mode A): pick a tier → POST /redeem → no redirect,
 *     swap to Mode B with the new code added.
 *   - "Select a Reward" (Mode B): pick an existing unused code → redirect
 *     to /discount/CODE?redirect=/cart so the customer sees it applied.
 *
 * Default mode on load:
 *   - Mode B if the customer has 1+ unused active codes (member.available_codes).
 *   - Otherwise Mode A.
 */
(function () {
  function formatDollars(n) {
    return Number.isInteger(n) ? n.toFixed(0) : n.toFixed(2);
  }

  function formatExpiry(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  function rewardOptionLabel(c) {
    const dollars = c.dollars != null ? `$${formatDollars(c.dollars)} off` : 'Reward';
    const exp = c.expires_at ? ` — expires ${formatExpiry(c.expires_at)}` : '';
    return `${dollars} — ${c.code}${exp}`;
  }

  async function initWidget(root) {
    if (!root || root.dataset.clInitialized === '1') return;
    root.dataset.clInitialized = '1';

    const API_BASE = (root.dataset.apiBase || '').replace(/\/$/, '');
    const email = root.dataset.customerEmail || '';
    if (!email || !API_BASE || API_BASE.includes('your-app.onrender.com')) return;

    const sub = root.querySelector('[data-cl-sub]');
    const discountWrap = root.querySelector('[data-cl-select-wrap]');
    const select = root.querySelector('[data-cl-select]');
    const rewardsWrap = root.querySelector('[data-cl-rewards-wrap]');
    const rewardsSelect = root.querySelector('[data-cl-rewards]');
    const showRewardsLink = root.querySelector('[data-cl-show-rewards]');
    const showDiscountLink = root.querySelector('[data-cl-show-discount]');
    const msg = root.querySelector('[data-cl-msg]');

    // Local state — kept in closure so mode-flips and post-redeem refresh
    // don't require another network round-trip.
    let availableCodes = [];
    let affordableTiers = [];
    let redemptionRate = 10;
    let redemptionValue = 0.50;

    function showOnly(which) {
      if (discountWrap) discountWrap.hidden = which !== 'discount';
      if (rewardsWrap) rewardsWrap.hidden = which !== 'rewards';
    }

    function renderDiscountMode() {
      if (!select) return;
      [...select.querySelectorAll('option:not([value=""])')].forEach(o => o.remove());
      affordableTiers.forEach(p => {
        const dollars = (p / redemptionRate) * redemptionValue;
        const opt = document.createElement('option');
        opt.value = String(p);
        opt.textContent = `$${formatDollars(dollars)} Off (${p} points)`;
        select.appendChild(opt);
      });
      select.value = '';
      select.disabled = false;
      // Toggle to rewards-mode is offered only when the customer has codes.
      if (showRewardsLink) showRewardsLink.hidden = availableCodes.length === 0;
      showOnly('discount');
    }

    function renderRewardsMode() {
      if (!rewardsSelect) return;
      [...rewardsSelect.querySelectorAll('option:not([value=""])')].forEach(o => o.remove());
      availableCodes.forEach(c => {
        const opt = document.createElement('option');
        opt.value = c.code;
        opt.textContent = rewardOptionLabel(c);
        rewardsSelect.appendChild(opt);
      });
      rewardsSelect.value = '';
      rewardsSelect.disabled = false;
      // Offer the "Generate a new discount" toggle only when the customer
      // can actually afford to redeem more — otherwise it leads nowhere.
      if (showDiscountLink) showDiscountLink.hidden = affordableTiers.length === 0;
      showOnly('rewards');
    }

    async function handleRedeem() {
      const pts = Number(select.value);
      if (!pts) return;
      select.disabled = true;
      if (msg) msg.textContent = 'Generating your discount code…';
      try {
        const res = await fetch(`${API_BASE}/api/loyalty-public/redeem`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, points: pts })
        });
        const data = await res.json();
        if (!res.ok || !data.success) {
          if (msg) msg.textContent = data.error || 'Could not redeem. Please try again.';
          select.disabled = false;
          select.value = '';
          return;
        }
        // Add the new code to local state, drop a tier the customer can no
        // longer afford, and swap to rewards-mode so the new code is one
        // click away from being applied.
        const tier = [
          { pts: 200,  dollars: 10, minOrder: 0 },
          { pts: 400,  dollars: 20, minOrder: 75 },
          { pts: 800,  dollars: 40, minOrder: 100 },
          { pts: 1600, dollars: 80, minOrder: 250 }
        ].find(t => t.pts === pts);
        availableCodes.unshift({
          code: data.code,
          dollars: tier ? tier.dollars : Number(data.discount_amount),
          min_order: tier ? tier.minOrder : 0,
          expires_at: data.expires_at
        });
        const newPoints = Number(data.new_total) || 0;
        affordableTiers = affordableTiers.filter(p => p <= newPoints);
        if (sub) sub.textContent = `You have ${newPoints} points! Pick a reward to apply.`;
        if (msg) msg.textContent = `New $${data.discount_amount} reward created — apply it below.`;
        renderRewardsMode();
      } catch (err) {
        if (msg) msg.textContent = 'Could not connect. Please try again.';
        select.disabled = false;
        select.value = '';
      }
    }

    function handleRewardSelect() {
      const code = rewardsSelect.value;
      if (!code) return;
      rewardsSelect.disabled = true;
      if (msg) msg.textContent = `Applying reward (${code})…`;
      window.location.href = `/discount/${encodeURIComponent(code)}?redirect=/checkout`;
    }

    try {
      const res = await fetch(`${API_BASE}/api/loyalty-public/${encodeURIComponent(email)}`);
      const data = await res.json();
      const member = data.member;
      const cfg = data.config || {};

      if (!member) {
        if (sub) sub.textContent = 'Place your first order to start earning points.';
        return;
      }

      const points = Number(member.loyalty_points) || 0;
      redemptionRate = Number(cfg.redemption_rate) || 10;
      redemptionValue = Number(cfg.redemption_value) || 0.50;
      const minPts = Number(cfg.min_redemption_pts) || 100;

      const tierPts = Array.isArray(cfg.ticketPts) && cfg.ticketPts.length
        ? cfg.ticketPts.slice().sort((a, b) => a - b)
        : [100, 200, 400, 800];

      affordableTiers = tierPts.filter(p => p <= points && p >= minPts);
      availableCodes = Array.isArray(member.available_codes) ? member.available_codes.slice() : [];

      if (sub) sub.textContent = `You have ${points} points! Redeem for discounts on this order.`;

      // Wire change handlers once.
      if (select) select.addEventListener('change', handleRedeem);
      if (rewardsSelect) rewardsSelect.addEventListener('change', handleRewardSelect);
      if (showRewardsLink) {
        showRewardsLink.addEventListener('click', e => { e.preventDefault(); renderRewardsMode(); });
      }
      if (showDiscountLink) {
        showDiscountLink.addEventListener('click', e => { e.preventDefault(); renderDiscountMode(); });
      }

      // Pick initial mode.
      if (availableCodes.length > 0) {
        renderRewardsMode();
      } else if (affordableTiers.length > 0) {
        renderDiscountMode();
      } else {
        // No codes and not enough points — keep both pickers hidden, show why.
        if (sub && points < minPts) {
          sub.textContent = `You have ${points} points. Earn ${minPts - points} more to unlock your first reward.`;
        }
      }
    } catch (err) {
      if (sub) sub.textContent = 'Could not load your points. Please refresh.';
    }
  }

  function scan() {
    document.querySelectorAll('[data-cart-loyalty]').forEach(initWidget);
  }

  // Initial scan
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }

  // Re-scan after cart drawer/cart updates (theme-agnostic)
  document.addEventListener('on:cart:after-merge', scan);
  document.addEventListener('dispatch:cart-drawer:refresh', scan);
  document.addEventListener('cart:refresh', scan);

  // Fallback: any time the drawer re-renders new markup, catch the fresh widget
  const mo = new MutationObserver(() => {
    document.querySelectorAll('[data-cart-loyalty]:not([data-cl-initialized="1"])').forEach(initWidget);
  });
  if (document.body) {
    mo.observe(document.body, { childList: true, subtree: true });
  } else {
    document.addEventListener('DOMContentLoaded', () => mo.observe(document.body, { childList: true, subtree: true }));
  }
})();
